import {Component, ChangeDetectionStrategy} from '@angular/core';

import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {ChartData, ChartOptions} from 'chart.js';
import {BaseChartDirective} from 'ng2-charts';
import {de} from 'date-fns/locale';

interface ParsedPoint {
  timestamp: string;
  value: number;
}

interface ComparisonRow {
  timestamp: string;
  dataset1Value: number | null;
  dataset2Value: number | null;
  isMismatch: boolean;
}

@Component({
  selector: 'app-chart-comparison-debug',
  imports: [FormsModule, RouterLink, BaseChartDirective],
  templateUrl: './chart-comparison-debug.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './chart-comparison-debug.component.css',
})
export class ChartComparisonDebugComponent {
  firstInput = '';
  secondInput = '';

  firstStatus = 'Paste dataset 1 and click "Generate charts".';
  secondStatus = 'Paste dataset 2 and click "Generate charts".';

  chartType: 'line' = 'line';

  firstChartData: ChartData<'line'> = this.createEmptyChartData('Dataset 1', '#1d4ed8');
  secondChartData: ChartData<'line'> = this.createEmptyChartData('Dataset 2', '#f97316');
  comparisonRows: ComparisonRow[] = [];

  readonly chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'minute',
          tooltipFormat: 'dd.MM.yyyy, HH:mm',
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Value',
        },
      },
    },
    plugins: {
      legend: {
        display: true,
      },
    },
  };

  generateCharts(): void {
    const firstParsed = this.parseInput(this.firstInput);
    const secondParsed = this.parseInput(this.secondInput);

    this.firstChartData = this.createChartDataFromPoints(firstParsed, 'Dataset 1', '#1d4ed8');
    this.secondChartData = this.createChartDataFromPoints(secondParsed, 'Dataset 2', '#f97316');

    this.firstStatus = firstParsed.length === 0
      ? 'No valid data rows found. Expected rows like: <node_id> <timestamp> <value>.'
      : `Parsed ${firstParsed.length} rows.`;
    this.secondStatus = secondParsed.length === 0
      ? 'No valid data rows found. Expected rows like: <node_id> <timestamp> <value>.'
      : `Parsed ${secondParsed.length} rows.`;

    this.comparisonRows = this.buildComparisonRows(firstParsed, secondParsed);
  }

  formatTimestamp(timestamp: string): string {
    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) {
      return timestamp;
    }

    return parsedDate.toLocaleString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Europe/Vienna',
    });
  }

  formatValue(value: number | null): string {
    if (value === null) {
      return '-';
    }

    return value.toLocaleString('de-AT', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 10,
    });
  }

  private parseInput(input: string): ParsedPoint[] {
    const parsed: ParsedPoint[] = [];
    const lines = input.split(/\r?\n/);
    const dataRowPattern = /(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}(?:[+-]\d{2}(?::?\d{2})?)?)\s+([-+]?\d[\d.,]*)$/;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        continue;
      }

      const rowMatch = trimmedLine.match(dataRowPattern);
      if (!rowMatch) {
        continue;
      }

      const timestamp = this.normalizeTimestamp(rowMatch[1]);
      const value = this.parseValue(rowMatch[2]);

      if (!timestamp || value === null) {
        continue;
      }

      parsed.push({timestamp, value});
    }

    return parsed.sort((first, second) => new Date(first.timestamp).getTime() - new Date(second.timestamp).getTime());
  }

  private createChartDataFromPoints(points: ParsedPoint[], label: string, color: string): ChartData<'line'> {
    if (points.length === 0) {
      return this.createEmptyChartData(label, color);
    }

    return {
      labels: points.map((point) => point.timestamp),
      datasets: [
        {
          label,
          data: points.map((point) => point.value),
          borderColor: color,
          backgroundColor: `${color}33`,
          pointRadius: 1.5,
          pointHoverRadius: 3,
          borderWidth: 2,
          tension: 0.15,
          fill: false,
        },
      ],
    };
  }

  private buildComparisonRows(firstPoints: ParsedPoint[], secondPoints: ParsedPoint[]): ComparisonRow[] {
    const firstMap = new Map<string, number>();
    const secondMap = new Map<string, number>();

    for (const point of firstPoints) {
      firstMap.set(point.timestamp, point.value);
    }

    for (const point of secondPoints) {
      secondMap.set(point.timestamp, point.value);
    }

    const timestamps = Array.from(new Set([...firstMap.keys(), ...secondMap.keys()]))
      .sort((first, second) => new Date(first).getTime() - new Date(second).getTime());

    return timestamps.map((timestamp) => {
      const dataset1Value = firstMap.has(timestamp) ? firstMap.get(timestamp)! : null;
      const dataset2Value = secondMap.has(timestamp) ? secondMap.get(timestamp)! : null;
      const isMismatch = !this.valuesMatch(dataset1Value, dataset2Value);

      return {
        timestamp,
        dataset1Value,
        dataset2Value,
        isMismatch,
      };
    });
  }

  private valuesMatch(first: number | null, second: number | null): boolean {
    if (first === null || second === null) {
      return false;
    }

    return Math.abs(first - second) < 1e-9;
  }

  private normalizeTimestamp(rawTimestamp: string): string | null {
    let normalized = rawTimestamp.trim().replace(/\s+/, 'T');

    if (/[+-]\d{2}$/.test(normalized)) {
      normalized = `${normalized}:00`;
    } else if (/[+-]\d{4}$/.test(normalized)) {
      normalized = `${normalized.slice(0, -2)}:${normalized.slice(-2)}`;
    }

    const parsedDate = new Date(normalized);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate.toISOString();
  }

  private parseValue(rawValue: string): number | null {
    let normalized = rawValue.trim().replace(/\s+/g, '');

    if (normalized.includes(',') && normalized.includes('.')) {
      if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = normalized.replace(/,/g, '');
      }
    } else if (normalized.includes(',')) {
      normalized = normalized.replace(',', '.');
    }

    const parsedValue = Number.parseFloat(normalized);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private createEmptyChartData(label: string, color: string): ChartData<'line'> {
    return {
      labels: [],
      datasets: [
        {
          label,
          data: [],
          borderColor: color,
          backgroundColor: `${color}33`,
          pointRadius: 1.5,
          pointHoverRadius: 3,
          borderWidth: 2,
          tension: 0.15,
          fill: false,
        },
      ],
    };
  }
}
